import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, Stack, Typography, Divider, FormControl, InputLabel, Select, MenuItem, Grid,
} from '@mui/material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import { admissionCycleService } from '../../services/admissions/admissionCycleService';
import { AdmissionCycle } from '../../types/admissions';
import { sessionsApi } from '../../services/sessions/sessionsApi';
import useTimezone from '../../hooks/useTimezone';
import RollingAdmissionsSwitch from './settings/RollingAdmissionsSwitch';

/**
 * Minimal cycle creator — just the essentials to get a Draft on the board:
 *   • Name + optional description
 *   • Session the cycle belongs to
 *   • Application-window dates + classes-begin date (defaults populated)
 *   • Application fee (optional; 0 = free)
 *
 * The cycle comes with EduBook's full application form. The rest (seats,
 * eligibility, fees, tests, interviews, offer terms, prospectus) is set up
 * in the cycle's Settings tab, or copied there from an earlier cycle.
 */

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (cycle: AdmissionCycle) => void;
}

interface Session {
  _id: string;
  name: string;
  status?: string;
}

interface FormValues {
  name: string;
  description: string;
  sessionId: string;
  applicationOpen: string;
  applicationClose: string;
  /** No closing date — rolling admissions. */
  rolling: boolean;
  admissionFeeDeadline: string;
  classesBeginOn: string;
  applicationFeeAmount: number;
}

const DEFAULT_WINDOW_DAYS = 30;
const DEFAULT_CLASSES_BEGIN_DAYS = 90;

const toIsoDate = (d: Date): string => d.toISOString().slice(0, 10);

const CreateCycleDialog: React.FC<Props> = ({ open, onClose, onCreated }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { getToday } = useTimezone();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoadingSessions(true);
      try {
        const list = await sessionsApi.list({ status: 'active' });
        if (!cancelled) setSessions(list);
      } catch (err: any) {
        if (!cancelled) enqueueSnackbar(err?.message || 'Failed to load sessions', { variant: 'error' });
      } finally {
        if (!cancelled) setLoadingSessions(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, enqueueSnackbar]);

  // Today in the school's timezone, at UTC midnight so adding days stays on whole dates.
  const now = new Date(`${getToday()}T00:00:00Z`);
  const defaultClose = new Date(now.getTime() + DEFAULT_WINDOW_DAYS * 24 * 60 * 60 * 1000);
  const defaultClassesBegin = new Date(now.getTime() + DEFAULT_CLASSES_BEGIN_DAYS * 24 * 60 * 60 * 1000);

  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      name: '',
      description: '',
      sessionId: '',
      applicationOpen: toIsoDate(now),
      applicationClose: toIsoDate(defaultClose),
      rolling: false,
      admissionFeeDeadline: toIsoDate(defaultClose),
      classesBeginOn: toIsoDate(defaultClassesBegin),
      applicationFeeAmount: 0,
    },
    validationSchema: yup.object({
      name: yup.string().trim().required('Cycle name is required').max(120),
      sessionId: yup.string().required('Pick a session'),
      applicationOpen: yup.string().required('Required'),
      applicationClose: yup.string().when('rolling', {
        is: true,
        then: s => s.notRequired(),
        otherwise: s => s.required('Required').test(
          'after-open',
          'Close date must be after open date',
          function (val) {
            if (!val || !this.parent.applicationOpen) return true;
            return new Date(val) > new Date(this.parent.applicationOpen);
          },
        ),
      }),
      admissionFeeDeadline: yup.string().required('Required'),
      classesBeginOn: yup.string().required('Required').test(
        'after-close',
        'Classes-begin should be on or after application close',
        function (val) {
          if (!val || this.parent.rolling || !this.parent.applicationClose) return true;
          return new Date(val) >= new Date(this.parent.applicationClose);
        },
      ),
      applicationFeeAmount: yup.number().min(0).required(),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const created = await admissionCycleService.create({
          name: values.name.trim(),
          description: values.description.trim() || undefined,
          sessionId: values.sessionId,
          schedule: {
            applicationOpen: new Date(values.applicationOpen) as any,
            applicationClose: values.rolling ? null : new Date(values.applicationClose) as any,
            admissionFeeDeadline: new Date(values.admissionFeeDeadline) as any,
            classesBeginOn: new Date(values.classesBeginOn) as any,
            meritPublishDates: [],
            documentReviewBy: null,
            testWindow: null,
            interviewWindow: null,
          },
          applicationFeeAmount: Number(values.applicationFeeAmount) || 0,
        });
        onCreated(created);
        formik.resetForm();
      } catch (err: any) {
        enqueueSnackbar(err?.message || 'Failed to create cycle', { variant: 'error' });
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleClose = () => {
    if (formik.isSubmitting) return;
    onClose();
    formik.resetForm();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <form onSubmit={formik.handleSubmit} noValidate>
        <DialogTitle component="div" sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight={700}>New admission cycle</Typography>
          <Typography variant="caption" color="text.secondary">
            Start with the dates. The cycle comes with EduBook’s full application form; set up the rest in its Settings, or copy it from an earlier cycle.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <TextField
              label="Cycle name"
              placeholder="e.g. Admissions 2026–27"
              name="name"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
              fullWidth autoFocus
            />
            <TextField
              label="Description (optional)"
              name="description"
              value={formik.values.description}
              onChange={formik.handleChange}
              fullWidth multiline minRows={2}
            />
            <FormControl fullWidth error={formik.touched.sessionId && Boolean(formik.errors.sessionId)}>
              <InputLabel>Session</InputLabel>
              <Select
                label="Session"
                name="sessionId"
                value={formik.values.sessionId}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={loadingSessions}
              >
                {sessions.map(s => (
                  <MenuItem key={s._id} value={s._id}>{s.name}</MenuItem>
                ))}
              </Select>
              {formik.touched.sessionId && formik.errors.sessionId && (
                <Typography variant="caption" color="error" sx={{ ml: 1.5, mt: 0.5 }}>
                  {formik.errors.sessionId}
                </Typography>
              )}
            </FormControl>

            <Divider />
            <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
              Schedule
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Applications open"
                  type="date"
                  name="applicationOpen"
                  value={formik.values.applicationOpen}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.applicationOpen && Boolean(formik.errors.applicationOpen)}
                  helperText={formik.touched.applicationOpen && formik.errors.applicationOpen}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Applications close"
                  type="date"
                  name="applicationClose"
                  value={formik.values.rolling ? '' : formik.values.applicationClose}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={formik.values.rolling}
                  error={!formik.values.rolling && formik.touched.applicationClose && Boolean(formik.errors.applicationClose)}
                  helperText={formik.values.rolling ? 'No closing date' : formik.touched.applicationClose && formik.errors.applicationClose}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <RollingAdmissionsSwitch checked={formik.values.rolling} onChange={rolling => formik.setFieldValue('rolling', rolling)} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Admission fee deadline"
                  type="date"
                  name="admissionFeeDeadline"
                  value={formik.values.admissionFeeDeadline}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.admissionFeeDeadline && Boolean(formik.errors.admissionFeeDeadline)}
                  helperText={formik.touched.admissionFeeDeadline && formik.errors.admissionFeeDeadline}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Classes begin"
                  type="date"
                  name="classesBeginOn"
                  value={formik.values.classesBeginOn}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.classesBeginOn && Boolean(formik.errors.classesBeginOn)}
                  helperText={formik.touched.classesBeginOn && formik.errors.classesBeginOn}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>

            <Divider />
            <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
              Application fee
            </Typography>
            <TextField
              label="Application fee amount"
              type="number"
              name="applicationFeeAmount"
              value={formik.values.applicationFeeAmount}
              onChange={formik.handleChange}
              helperText="Set to 0 for a free application (parents skip the fee-pending step)."
              inputProps={{ min: 0, step: 1 }}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} disabled={formik.isSubmitting}>Cancel</Button>
          <Button
            type="submit"
            variant="contained"
            disableElevation
            disabled={formik.isSubmitting || !formik.isValid}
          >
            {formik.isSubmitting ? 'Creating…' : 'Create cycle'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateCycleDialog;
